import { useSupportedCorporations } from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { EditIcon01, ProceedIcon } from '@commenda/icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Loading,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher, getCorporationNameFromWorkRequest } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import {
  CorporationType,
  Country,
  Currency,
  IncorporationRequestStatus,
  LegacyDocumentType,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { getFileWithType } from '../../../common/helpers';
import {
  DocumentSummaryField,
  IncorporationFormHeading,
  SubmitWorkRequestModalKS,
  SummaryFieldKS,
} from '../../common';
import CorporateShareholdersSummary from '../../common/CorporateShareholdersSummary';
import { extractCorporateShareholders } from '../../common/corporateStakeholder/utils';
import CapTable from '../../EquityInfo/CapTable';
import { CompanyValuationSummary } from '../../EquityInfo/CompanyValuationSummary';
import { useEquityFormState } from '../../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../../EquityInfo/hooks/useEquityResources';
import INPVTLTDFounderField from './INPVTLTDFounderField';

const SummarySection = ({
  setActiveStep,
  onProceed,
  data,
  mutate,
  hideActionButtons = false,
  isReviewSummary = false,
  setNotification,
  token,
  corporationId,
}: {
  setActiveStep: (stepIdx: number) => void;
  onProceed: (idx: number) => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  hideActionButtons?: boolean;
  isReviewSummary?: boolean;
  token: string;
  setNotification: TSetNotification;
  corporationId: number;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateCorporations,
    isLoading,
    isError,
  } = useEquityResources(data?.companyId, corporationId);

  const { capTableData, equityInfo } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: undefined,
    corporationId,
    config: {
      defaultShareCount: 10_000_000,
      defaultSharePrice: 0.00001,
      currency: Currency.INR,
    },
    onSubmitSuccess: undefined,
    validateStakeholdersAndGetIdsWithError: undefined,
    corporationType: CorporationType.PvtLtd,
    incorporationCountry: Country.IN,
  });

  const [isSubmitting, setSubmitting] = useState(false);

  const { data: corporationMetaData } = useSupportedCorporations();

  // Get individual shareholders from cap table transactions
  const individualShareholders = useMemo(() => {
    if (!capTableTransactions || !directorsData?.directors) return [];

    // Get director IDs from cap table transactions
    const directorIdsFromCapTable = capTableTransactions
      .map((tx) => {
        const director = tx.stakeholding?.stakeholder?.director;
        if (!director) return null; // skip corporate shareholders
        return director.id;
      })
      .filter(Boolean);

    // Get full director data from directorsData
    const shareholders = directorsData.directors.filter((director) =>
      directorIdsFromCapTable.includes(director.id)
    );

    return shareholders;
  }, [capTableTransactions, directorsData]);

  const optForVirtualAddress = data?.requestData?.isVirtualAddress;

  // Get all required documents for India Pvt Ltd incorporation
  const currentRentalAgreement = getFileWithType(
    data?.files || [],
    LegacyDocumentType.RentalAgreement
  );

  const currentCompanyAddressProof = getFileWithType(
    data?.files || [],
    LegacyDocumentType.AddressProof
  );

  const currentIncorporationDocument = getFileWithType(
    data?.files || [],
    LegacyDocumentType.IndPvtIncorporationDocument
  );

  const currentSignedIncorporationDocument = getFileWithType(
    data?.files || [],
    LegacyDocumentType.SignedIndPvtIncorporationDocument
  );

  const currentUnsignedIncorporationDocument = getFileWithType(
    data?.files || [],
    LegacyDocumentType.UnsignedIndPvtIncorporationDocument
  );

  const currentCertificateOfIncorporation = getFileWithType(
    data?.files || [],
    LegacyDocumentType.CertificateOfIncorporation
  );

  const currentCertificateOfIncorporationByMCA = getFileWithType(
    data?.files || [],
    LegacyDocumentType.CertificateOfIncorporationByMCA
  );

  const currentPANCard = getFileWithType(
    data?.files || [],
    LegacyDocumentType.PanCard
  );

  const currentTAN = getFileWithType(data?.files || [], LegacyDocumentType.TAN);

  const currentINC20A = getFileWithType(
    data?.files || [],
    LegacyDocumentType.INC20A
  );

  const currentBankStatementOfSubscriptionAmount = getFileWithType(
    data?.files || [],
    LegacyDocumentType.BankStatementsOfSubscriptionAmount
  );

  const currentOfficePhotoWithNameBoard = getFileWithType(
    data?.files || [],
    LegacyDocumentType.OfficePhotoWithNameBoard
  );

  const currentDirectorInOfficeProof = getFileWithType(
    data?.files || [],
    LegacyDocumentType.DirectorInOfficeProof
  );

  const currentShareCertificateStampPaper = getFileWithType(
    data?.files || [],
    LegacyDocumentType.ShareCertificateStampPaper
  );

  const currentSignedShareCertificateStampPaper = getFileWithType(
    data?.files || [],
    LegacyDocumentType.ShareCertificateStampPaperSigned
  );

  const currentUnsignedShareCertificateStampPaper = getFileWithType(
    data?.files || [],
    LegacyDocumentType.UnsignedShareCertificateStampPaper
  );

  const currentADTForm = getFileWithType(
    data?.files || [],
    LegacyDocumentType.ADTForm
  );

  const currentUnsignedADTForm = getFileWithType(
    data?.files || [],
    LegacyDocumentType.UnsignedADTForm
  );

  const currentCACertificate = getFileWithType(
    data?.files || [],
    LegacyDocumentType.CACertificate
  );

  const currentCorporationDetails =
    corporationMetaData?.corporations?.[
      data?.corporation?.incorporationCountry
    ]?.[data?.corporation?.jurisdiction]?.[data?.corporation?.corporationType];

  const optForIndianResidentDirector =
    data?.requestData?.optForIndianResidentDirector;

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  const onSubmit = async () => {
    setSubmitting(true);

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          status: IncorporationRequestStatus.Submitted,
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onProceed(3);
    router.back();
  };

  if (isLoading || isError) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-8 max-w-[700px]">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please verify the following information before submitting. Once submitted, you may not be able to make changes."
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
          "
          >
            Company Info
          </h4>
          {hideActionButtons ? null : (
            <EditIcon01
              className="cursor-pointer"
              onClick={() => setActiveStep(0)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-sm">
          <div className="flex flex-col gap-2 p-6">
            <SummaryFieldKS
              heading="Entity Name 1"
              value={getCorporationNameFromWorkRequest(data?.requestData)}
            />
            <SummaryFieldKS
              heading="Entity Name 2"
              value={getCorporationNameFromWorkRequest(
                data?.requestData,
                'companyLegalName2'
              )}
            />
            <SummaryFieldKS
              heading="Entity Name 3"
              value={getCorporationNameFromWorkRequest(
                data?.requestData,
                'companyLegalName3'
              )}
            />
            <SummaryFieldKS
              heading="Contact Email"
              value={data?.corporation?.contactEmail || ''}
            />
            <SummaryFieldKS
              heading="Contact Number"
              value={data?.corporation?.contactPhoneNumber || ''}
            />
            <SummaryFieldKS
              heading="Corporation Type"
              value={
                currentCorporationDetails?.appCopy?.corporationTypeShortName
              }
            />
            <SummaryFieldKS
              heading="Industry Type"
              value={data?.requestData?.industry}
            />

            <SummaryFieldKS
              heading="Virtual Address"
              value={optForVirtualAddress ? 'Yes' : 'No'}
            />

            <SummaryFieldKS
              heading="Opting for Commenda's Indian Resident Director Service"
              value={optForIndianResidentDirector ? 'Yes' : 'No'}
            />

            <SummaryFieldKS
              heading="Business Description"
              value={data?.requestData?.businessDescription}
            />

            {currentCompanyAddressProof?.id && !optForVirtualAddress ? (
              <DocumentSummaryField
                onClick={() => openFile(currentCompanyAddressProof.id)}
                title="Address Proof"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentRentalAgreement?.id &&
            !optForVirtualAddress &&
            data?.requestData?.isOnRentedProperty ? (
              <DocumentSummaryField
                onClick={() => openFile(currentRentalAgreement.id)}
                title="Rental Agreement"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentIncorporationDocument?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentIncorporationDocument.id)}
                title="Pvt Ltd Incorporation Document"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentSignedIncorporationDocument?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentSignedIncorporationDocument.id)}
                title="Signed Pvt Ltd Incorporation Document"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentUnsignedIncorporationDocument?.id ? (
              <DocumentSummaryField
                onClick={() =>
                  openFile(currentUnsignedIncorporationDocument.id)
                }
                title="Unsigned Pvt Ltd Incorporation Document"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentCertificateOfIncorporation?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentCertificateOfIncorporation.id)}
                title="Certificate of Incorporation"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentCertificateOfIncorporationByMCA?.id ? (
              <DocumentSummaryField
                onClick={() =>
                  openFile(currentCertificateOfIncorporationByMCA.id)
                }
                title="MCA Certificate of Incorporation"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentPANCard?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentPANCard.id)}
                title="PAN Card"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentTAN?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentTAN.id)}
                title="TAN"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentINC20A?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentINC20A.id)}
                title="INC-20A"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentBankStatementOfSubscriptionAmount?.id ? (
              <DocumentSummaryField
                onClick={() =>
                  openFile(currentBankStatementOfSubscriptionAmount.id)
                }
                title="Bank Statement of Subscription Amount"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentOfficePhotoWithNameBoard?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentOfficePhotoWithNameBoard.id)}
                title="Office Photo with Name Board"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentDirectorInOfficeProof?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentDirectorInOfficeProof.id)}
                title="Director in Office Proof"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentShareCertificateStampPaper?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentShareCertificateStampPaper.id)}
                title="Share Certificate Stamp Paper"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentSignedShareCertificateStampPaper?.id ? (
              <DocumentSummaryField
                onClick={() =>
                  openFile(currentSignedShareCertificateStampPaper.id)
                }
                title="Signed Share Certificate Stamp Paper"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentUnsignedShareCertificateStampPaper?.id ? (
              <DocumentSummaryField
                onClick={() =>
                  openFile(currentUnsignedShareCertificateStampPaper.id)
                }
                title="Unsigned Share Certificate Stamp Paper"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentADTForm?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentADTForm.id)}
                title="ADT Form"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentUnsignedADTForm?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentUnsignedADTForm.id)}
                title="Unsigned ADT Form"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
            {currentCACertificate?.id ? (
              <DocumentSummaryField
                onClick={() => openFile(currentCACertificate.id)}
                title="CA Certificate"
                className="grid grid-cols-2 gap-8"
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
          "
          >
            Directors
          </h4>
          {hideActionButtons ? null : (
            <EditIcon01
              className="cursor-pointer"
              onClick={() => setActiveStep(1)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-sm">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {data?.directors
              ?.filter((dir) =>
                dir.corporationHasDirector?.find(
                  (chd) =>
                    chd.corporationId === corporationId &&
                    (chd.additionalDirectorInformation?.isDirector ||
                      individualShareholders.some(
                        (shareholder) => shareholder.id === dir.id
                      ))
                )
              )
              .map((dir) => (
                <AccordionItem
                  className="AccordionItem border border-border-primary rounded-md"
                  value={dir.id?.toString()}
                  key={dir.id}
                >
                  <AccordionTrigger className="w-full ">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className=" p-4 pt-0">
                    <INPVTLTDFounderField
                      key={dir.id}
                      director={dir}
                      data={data}
                      showDocuments
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </div>
      </div>

      {individualShareholders.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Individual Shareholders
            </h4>
            {hideActionButtons ? null : (
              <EditIcon01
                className="cursor-pointer"
                onClick={() => setActiveStep(2)}
              />
            )}
          </div>
          <div className="flex flex-col gap-3 rounded-sm">
            <Accordion
              className="AccordionRoot flex flex-col gap-3"
              type="single"
              collapsible
            >
              {individualShareholders.map((dir) => (
                <AccordionItem
                  className="AccordionItem border border-border-primary rounded-md"
                  value={dir.id?.toString()}
                  key={dir.id}
                >
                  <AccordionTrigger className="w-full ">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className=" p-4 pt-0">
                    <INPVTLTDFounderField
                      key={dir.id}
                      director={dir}
                      data={data}
                      showDocuments
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      )}

      {extractCorporateShareholders(capTableTransactions).length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Corporate Shareholders
            </h4>
            {hideActionButtons ? null : (
              <EditIcon01
                className="cursor-pointer"
                onClick={() => setActiveStep(2)}
              />
            )}
          </div>
          <CorporateShareholdersSummary
            corporateShareholders={extractCorporateShareholders(
              capTableTransactions
            )}
            directorsData={directorsData?.directors}
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="body-16-bold text-text-primary">Ownership Summary</h4>
        </div>
        <CompanyValuationSummary
          numberOfShares={equityInfo.shareCount}
          sharePrice={equityInfo.price}
          currency={{
            countryCode: 'en-IN',
            name: 'INR',
            style: '₹',
          }}
        />
        <div className="overflow-clip">
          <CapTable
            data={capTableData}
            isNumberOfSharesApplicable
            corporationType={CorporationType.PvtLtd}
            incorporationCountry={Country.IN}
            hideCapitalContribution
          />
        </div>
      </div>

      {hideActionButtons ? (
        <Button className="w-fit" onClick={() => router.push('/')}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back to Home</span>
        </Button>
      ) : (
        <div className="flex gap-4">
          <Button variant="secondary" onClick={() => setActiveStep(2)}>
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
          <SubmitWorkRequestModalKS
            isSubmitting={isSubmitting}
            description="This action is irreversible. Once submitted, the details will be
            locked for filing. Please review before submitting."
            onSubmit={onSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default SummarySection;
